/**
 * "Planner suggested…" chips for a capability's open proposals —
 * `product_map_revisions` rows with `changeKind: 'proposal_rejected'`: the
 * planning agent tried to write a field a human had locked, so the write was
 * refused and recorded for a human to accept or dismiss. One-click Apply
 * calls `POST /product-map/proposals/:revisionId/accept`.
 *
 * @module features/product-map/components/CapabilityProposals
 */

import { Sparkle } from '@phosphor-icons/react';
import { Button, Text } from '@chatsdet/ui';
import { useAcceptProposal } from '../useProductMap';
import type { ProductMapRevision } from '../types';

function summarizeProposedFields(proposal: ProductMapRevision): string {
  const fields = Object.keys(proposal.proposedJson ?? {});
  if (fields.length === 0) return 'a change';
  return fields.join(', ');
}

/**
 * A rule proposal's `proposedJson` is either `{statement}` (the agent tried
 * to reword a human-owned rule) or `{status: 'deprecated'}` (the agent tried
 * to remove one) — see `repositories/product-map/rules.ts`'s
 * `updateRuleFromAgent`/`deprecateRuleFromAgent`. `snapshotJson.statement` is
 * the rule's current (human-locked) statement either way.
 */
function describeRuleProposal(proposal: ProductMapRevision): string {
  const current = typeof proposal.snapshotJson.statement === 'string' ? proposal.snapshotJson.statement : 'this rule';
  const proposedStatement =
    typeof proposal.proposedJson?.statement === 'string' ? proposal.proposedJson.statement : null;
  if (proposedStatement) {
    return `Planner suggested rewording this rule: "${current}" → "${proposedStatement}"`;
  }
  if (proposal.proposedJson?.status === 'deprecated') {
    return `Planner suggested removing this rule: "${current}"`;
  }
  return `Planner suggested a change to this rule: "${current}"`;
}

type Props = {
  capabilityId: string;
  proposals: ProductMapRevision[];
};

export function CapabilityProposals({ capabilityId, proposals }: Props) {
  const acceptProposal = useAcceptProposal();

  if (proposals.length === 0) return null;

  return (
    <div className="space-y-2 rounded-md border border-dashed border-primary/40 bg-primary/5 p-3">
      {proposals.map((proposal) => (
        <div key={proposal.id} className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-start gap-2">
            <Sparkle size={14} weight="fill" className="mt-0.5 shrink-0 text-primary" />
            <Text size="xs" className="text-foreground">
              {proposal.entityType === 'rule' ? (
                describeRuleProposal(proposal)
              ) : (
                <>
                  Planner suggested updating <span className="font-medium">{summarizeProposedFields(proposal)}</span>{' '}
                  — locked by your edit.
                </>
              )}
            </Text>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="shrink-0"
            isDisabled={acceptProposal.isPending}
            onPress={() => acceptProposal.mutate({ revisionId: proposal.id, capabilityId })}
          >
            Apply
          </Button>
        </div>
      ))}
    </div>
  );
}
